<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { dateTimeFormat } from '@/commons/composables/transformations.ts'
import jwtService from '@/app/services/jwt.service.ts'
import router from '@/router'
import Pagination from '@/commons/components/Pagination.vue'
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import {useInstitutionStore} from "@/domains/home/services/nir/associate-registers/stores";
import {useNIRApplicationsStore} from "@/domains/home/services/nir/registration/stores";
import AppModal from "@/components/ui/AppModal.vue";
import TaskView from "@/domains/home/services/nir/registration/views/TaskView.vue";

const NAME_MAX_LENGTH = 60

const loading = ref(false)
const page = ref(1)
const limit = ref(16)
const store = useNIRApplicationsStore()
const configStore = useFormsStore()
const institutionStore = useInstitutionStore()

const nameFilter = ref('')
const trackingNumberFilter = ref('')
const entityTypeFilter = ref('')
const subEntityTypeFilter = ref('')
const statusFilter = ref('')
const institutionFilter = ref('')

const viewModalOpen = ref(false);
const selectedTaskId = ref("")

onMounted(() => {
  configStore.fetchPrimaryEntityTypes()
  institutionStore.fetchInstitutions(1, 300)
  fetch()
  localStorage.removeItem('applicationId')
})

function fetch() {
  loading.value = true
  store
      .fetchForcedApplications(
          trackingNumberFilter.value,
          nameFilter.value,
          institutionFilter.value,
          entityTypeFilter.value,
          subEntityTypeFilter.value,
          statusFilter.value,
          page.value,
          limit.value,
      )
      .then(() => {
        loading.value = false
      })
}

function institutionName(id: string) {
  return institutionStore.institutions?.find((i) => i.id === id)?.name || '--'
}

function open(id:string){
  viewModalOpen.value = true
  selectedTaskId.value = id
}

function update(pg: number) {
  page.value = pg
  fetch()
}

function fetchSubTypes() {
  configStore.fetchSubEntityTypes(entityTypeFilter.value)
  fetch()
}

function truncateName(name: string) {
  if (name.length <= NAME_MAX_LENGTH) return name
  return name.slice(0, NAME_MAX_LENGTH) + '…'
}

</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white">
    <div class="grid grid-cols-6 gap-2 py-2">
      <input
          v-model="trackingNumberFilter"
          @change="fetch"
          type="text"
          placeholder="Tracking Number"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
      <input
          v-model="nameFilter"
          @change="fetch"
          type="text"
          placeholder="Search name..."
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
      <select
          v-model="institutionFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
      >
        <option value="">Select Associate Register</option>
        <option
            v-for="(institution, idx) in institutionStore.institutions"
            :key="idx"
            :value="institution.id"
        >
          {{ institution.name }}
        </option>
      </select>
      <select
          v-model="entityTypeFilter"
          @change="fetchSubTypes"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
      >
        <option value="">Select Entity Type</option>
        <option v-for="(type, idx) in configStore.primaryEntityTypes" :key="idx" :value="type.id">
          {{ type.name }}
        </option>
      </select>

      <select
          v-model="subEntityTypeFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
      >
        <option value="">Select Sub Entity Type</option>
        <option v-for="(type, idx) in configStore.subEntityTypes" :key="idx" :value="type.id">
          {{ type.name }}
        </option>
      </select>
      <select
          v-model="statusFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
      >
        <option value="">Select Status</option>
        <option value="DRAFT">Draft</option>
        <option value="SUBMITTED">Submitted</option>
        <option value="PREAPPROVAL">Forced</option>
      </select>
    </div>

    <!-- Table Section -->
    <div class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="min-w-full text-xs whitespace-nowrap">
          <thead class="bg-gray-100 border-b-2 border-gray-200 sticky top-0">
          <tr>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              #
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Interim ERN
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Name
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Type
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Sub Type
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Associate Register
            </th>
            <th
                class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Status
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Submitted
            </th>
            <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
            >
              Created
            </th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="(application, idx) in store.forcedApplications"
              :key="idx"
              class="hover:bg-blue-50/50 transition-all duration-150"
          >
            <td class="py-3 px-4 border-b border-gray-200">
              <span>{{ idx + 1 }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 font-semibold">
              <span v-if="application.interimErn">{{ application.interimErn }}</span>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 font-semibold">
              <div v-if="application.name" class="relative group inline-block">
                <span>{{ truncateName(application.name) }}</span>
                <div
                    v-if="application.name.length > NAME_MAX_LENGTH"
                    class="absolute left-0 top-full mt-1 z-20 hidden group-hover:block w-max max-w-md whitespace-normal break-words bg-gray-900 text-white text-xs font-normal rounded-lg px-3 py-2 shadow-lg"
                >
                  {{ application.name }}
                </div>
              </div>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span>{{ application.type }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span v-if="application.subType">{{ application.subType }}</span>
              <span v-else class="text-gray-400">--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-primary">
              <div class="flex gap-2">
                <i class="fas fa-bank my-auto"></i>
                <span class="my-auto">{{ institutionName(application.institutionId) }}</span>
              </div>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-center whitespace-nowrap">
                <span
                    class="text-xs font-medium px-3 py-1 rounded-full inline-block whitespace-nowrap"
                    :class="{
                    'bg-gray-100 text-gray-400': application.status === 'DRAFT',
                    'bg-gray-100 text-gray-300': application.status === 'INITIATED',
                    'bg-gray-100 text-gray-800': application.status === 'SUBMITTED',
                    'bg-success-50 text-success-600 border border-success-200':
                      application.status === 'PREAPPROVAL',
                      'bg-warning-50 text-success-600 border border-warning-400':
                      application.status === 'LINKED',
                    'bg-primary-100 text-primary-800': application.status === 'ASSOCIATE_REGISTER_PROCESSING',
                    'bg-primary-100 text-primary-600': application.status === 'ESCALATED',
                    'bg-success-100 text-success-800': application.status === 'CLEARED',
                    'bg-danger-100 text-danger-800': application.status === 'REJECTED',
                    'bg-warning-700 text-white':
                      application.status === 'QUERIED' || application.status === 'REGISTER_QUERIED',
                  }"
                >
                  <label v-if="application.status == 'ESCALATED'">BEING PROCESSED</label>
                  <label v-else>{{ application.status.replace(/_/g, ' ').toUpperCase() }}</label>
                </span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
                <span v-if="application.submittedAt">{{
                    dateTimeFormat(application.submittedAt)
                  }}</span>
              <span v-else>--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
              <span>{{ dateTimeFormat(application.createdAt) }}</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <Pagination
          :pagination="store.pagination"
          :page="page"
          :limit="limit"
          label="applications"
          @update="update"
      />
    </div>
  </div>

  <AppModal v-model="viewModalOpen" xl6>
    <TaskView :id="selectedTaskId"/>
  </AppModal>
</template>

<style scoped></style>